import React, { useCallback, useMemo } from 'react';
import {
  Button,
  Card,
  Input,
  InputNumber,
  Select,
  Segmented,
  Space,
  Tag,
  Tooltip,
  Typography,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  DatabaseOutlined,
  SafetyCertificateOutlined,
  ExperimentOutlined,
  BarChartOutlined,
  InfoCircleOutlined,
	BranchesOutlined,
} from '@ant-design/icons';

import { nanoid } from 'nanoid';

import {
  ProtocolFlowNode,
  ProtocolFlowEdge,
  ConditionalNodeConfig,
  ConditionalNodeCondition,
  ProtocolConditionType,
  ProtocolConditionCategory,
  PROTOCOL_CONDITION_CATALOG,
  CONDITION_CATEGORY_META,
  ConditionOperator,
  StepType,
  PROTOCOL_NODE_CONFIG,
} from '../../../../../../types/lab/ProtocolTypes';

const { Text } = Typography;

// ── Operator human labels ─────────────────────────────────────────────────────
const OPERATOR_LABELS: Record<ConditionOperator, string> = {
  '=':           '= equals',
  '==':          '= equals',
  '!=':          '≠ not equals',
  '>':           '> greater than',
  '>=':          '≥ greater or equal',
  '<':           '< less than',
  '<=':          '≤ less or equal',
  contains:      'contains',
  not_contains:  'does not contain',
  starts_with:   'starts with',
  ends_with:     'ends with',
  in:            'is one of',
  not_in:        'is not one of',
  contains_any:  'has any of (any selected)',
  contains_all:  'has all of (all selected)',
  includes:      'includes value',
  excludes:      'does not include',
  equals_exactly: 'is exactly',
  regex:         'matches regex',
  is_empty:      'is empty',
  is_not_empty:  'is not empty',
  range:         'is between (range)',
};

// ── Category icon map ─────────────────────────────────────────────────────────
const CATEGORY_ICON: Record<ProtocolConditionCategory, React.ReactNode> = {
  step_outcome:  <CheckCircleOutlined style={{ color: '#10b981' }} />,
  field_value:   <DatabaseOutlined style={{ color: '#3b82f6' }} />,
  qc_result:     <SafetyCertificateOutlined style={{ color: '#f59e0b' }} />,
  sample_data:   <ExperimentOutlined style={{ color: '#8b5cf6' }} />,
  results_data:  <BarChartOutlined style={{ color: '#ec4899' }} />,
};

// ── Lab step types that can be referenced in step-ref selects ─────────────────
const STEP_REF_TYPES: StepType[] = ['manual', 'automated', 'qc', 'results', 'aliquot'];

// ── Helpers ───────────────────────────────────────────────────────────────────

/** BFS backwards through edges to collect ancestor node IDs. */
function getUpstreamNodes(
  nodeId: string,
  nodes: ProtocolFlowNode[],
  edges: ProtocolFlowEdge[],
): ProtocolFlowNode[] {
  const visited = new Set<string>();
  const queue = [nodeId];
  while (queue.length > 0) {
    const current = queue.shift()!;
    const parents = edges
      .filter((e) => e.target === current)
      .map((e) => e.source);
    for (const p of parents) {
      if (!visited.has(p)) {
        visited.add(p);
        queue.push(p);
      }
    }
  }
  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  // Only return lab-step nodes (not start/end/conditional); exclude the node itself
  return Array.from(visited)
    .map((id) => nodeById.get(id))
    .filter((n): n is ProtocolFlowNode => !!n && STEP_REF_TYPES.includes(n.data.step_type));
}

/** Extract field info from a node's configuration.required_fields. */
function getNodeFields(node: ProtocolFlowNode): { key: string; label: string; field_type?: string; validation_rules?: any }[] {
  const fields = node.data.configuration?.required_fields ?? [];
  return fields
    .filter((f) => f && typeof f === 'object' && 'field_name' in f)
    .map((f: any) => ({
      key: f.field_name,
      label: f.field_name,
      field_type: f.field_type,
      validation_rules: f.validation_rules,
    }));
}

// ── Field-type → allowed operators ───────────────────────────────────────────
const FIELD_TYPE_OPERATORS: Record<string, ConditionOperator[]> = {
  text:     ['=', '!=', 'contains', 'not_contains', 'starts_with', 'ends_with', 'regex', 'is_empty', 'is_not_empty'],
  number:   ['=', '!=', '>', '>=', '<', '<=', 'range', 'is_empty', 'is_not_empty'],
  boolean:  ['=', '!='],
  dropdown: ['includes', 'excludes', 'equals_exactly', 'contains_any', 'contains_all', '=', '!=', 'in', 'not_in', 'is_empty', 'is_not_empty'],
  date:     ['=', '!=', '>', '>=', '<', '<=', 'range', 'is_empty', 'is_not_empty'],
  datetime: ['=', '!=', '>', '>=', '<', '<=', 'range', 'is_empty', 'is_not_empty'],
  file:     ['is_empty', 'is_not_empty'],
};

function getOperatorsForFieldType(
  fieldType: string | undefined,
  catalogOperators: ConditionOperator[],
): ConditionOperator[] {
  if (!fieldType) return catalogOperators;
  const allowed = FIELD_TYPE_OPERATORS[fieldType] ?? catalogOperators;
  return catalogOperators.filter((op) => (allowed as string[]).includes(op));
}


/** Auto-generate a plain-English label for a condition row. */
function buildConditionLabel(
  cond: ConditionalNodeCondition,
  upstreamNodes: ProtocolFlowNode[],
): string {
  const meta = PROTOCOL_CONDITION_CATALOG[cond.type];
  if (!meta) return cond.type;

  const stepName = cond.step_node_id
    ? (upstreamNodes.find((n) => n.id === cond.step_node_id)?.data.label ?? `Step ${cond.step_number_ref ?? '?'}`)
    : '';

  const fieldPart = cond.field_key ? ` › ${cond.field_key}` : '';
  const opPart = cond.operator ? ` ${OPERATOR_LABELS[cond.operator] ?? cond.operator}` : '';
  const valuePart = cond.value !== undefined && cond.value !== null && cond.value !== ''
    ? ` "${cond.value}"`
    : '';

  switch (cond.type) {
    case 'step_completed':       return `"${stepName}" completed successfully`;
    case 'step_failed':          return `"${stepName}" failed`;
    case 'step_timed_out':       return `"${stepName}" timed out`;
    case 'step_duration_exceeded': return `"${stepName}" duration${opPart}${valuePart} min`;
    case 'field_value':          return `"${stepName}"${fieldPart}${opPart}${valuePart}`;
    case 'qc_passed':            return `QC "${stepName}" passed`;
    case 'qc_failed':            return `QC "${stepName}" failed`;
    case 'qc_metric_value':      return `QC "${stepName}"${fieldPart}${opPart}${valuePart}`;
    case 'sample_status':        return `Sample status${opPart}${valuePart}`;
    case 'sample_type':          return `Sample type${opPart}${valuePart}`;
    case 'sample_priority':      return `Sample priority${opPart}${valuePart}`;
    default:                     return meta.label;
  }
}

// ── Props ─────────────────────────────────────────────────────────────────────
export interface ConditionalNodeEditorProps {
  nodeId: string;
  allNodes: ProtocolFlowNode[];
  allEdges: ProtocolFlowEdge[];
  config: ConditionalNodeConfig | undefined;
  onChange: (config: ConditionalNodeConfig) => void;
}

// ── Default config ────────────────────────────────────────────────────────────
const DEFAULT_CONFIG: ConditionalNodeConfig = {
  group_operator: 'AND',
  conditions: [],
};

// ── Main Component ────────────────────────────────────────────────────────────
const ConditionalNodeEditor: React.FC<ConditionalNodeEditorProps> = ({
  nodeId,
  allNodes,
  allEdges,
  config,
  onChange,
}) => {
  const cfg = config ?? DEFAULT_CONFIG;

  // Upstream step nodes (ancestors only)
  const upstreamNodes = useMemo(
    () => getUpstreamNodes(nodeId, allNodes, allEdges),
    [nodeId, allNodes, allEdges],
  );

  // ── Mutators ──────────────────────────────────────────────────────────────
  const patchCondition = useCallback(
    (id: string, patch: Partial<ConditionalNodeCondition>) => {
      const next = cfg.conditions.map((c) => {
        if (c.id !== id) return c;
        const merged = { ...c, ...patch };
        // Re-generate label on any change
        merged.label = buildConditionLabel(merged, upstreamNodes);
        // Clear field_key if step_ref changed
        if ('step_node_id' in patch) merged.field_key = undefined;
        // Clear operator/value if field_key changed (field type may differ)
        if ('field_key' in patch) {
          merged.operator = undefined;
          merged.value = undefined;
        }
        // Clear operator/value if type changed
        if ('type' in patch) {
          merged.operator = undefined;
          merged.value = undefined;
          merged.field_key = undefined;
        }
        return merged;
      });
      onChange({ ...cfg, conditions: next });
    },
    [cfg, onChange, upstreamNodes],
  );

  const addCondition = useCallback(() => {
    onChange({
      ...cfg,
      conditions: [
        ...cfg.conditions,
        {
          id: nanoid(8),
          type: 'step_completed',
          label: '',
        } satisfies ConditionalNodeCondition,
      ],
    });
  }, [cfg, onChange]);

  const removeCondition = useCallback(
    (id: string) => {
      onChange({ ...cfg, conditions: cfg.conditions.filter((c) => c.id !== id) });
    },
    [cfg, onChange],
  );

  // ── Condition type picker options (grouped) ───────────────────────────────
  const typeOptions = useMemo(() => {
    const groups: Record<ProtocolConditionCategory, { value: ProtocolConditionType; label: React.ReactNode }[]> = {
      step_outcome:  [],
      field_value:   [],
      qc_result:     [],
      sample_data:   [],
      results_data:  [],
    };
    (Object.keys(PROTOCOL_CONDITION_CATALOG) as ProtocolConditionType[]).forEach((key) => {
      const entry = PROTOCOL_CONDITION_CATALOG[key];
      groups[entry.category].push({
        value: key,
        label: (
          <Space size={4}>
            {CATEGORY_ICON[entry.category]}
            <span>{entry.label}</span>
          </Space>
        ),
      });
    });
    return (Object.keys(groups) as ProtocolConditionCategory[]).map((cat) => ({
      label: (
        <Space size={4}>
          {CATEGORY_ICON[cat]}
          <Text strong style={{ fontSize: 11 }}>{CONDITION_CATEGORY_META[cat].label}</Text>
        </Space>
      ),
      options: groups[cat],
    }));
  }, []);

  // ── Render one condition row ───────────────────────────────────────────────
  const renderConditionRow = (cond: ConditionalNodeCondition, idx: number) => {
    const meta = PROTOCOL_CONDITION_CATALOG[cond.type];
    const selectedUpstream = upstreamNodes.find((n) => n.id === cond.step_node_id);
    const availableFields = selectedUpstream ? getNodeFields(selectedUpstream) : [];

    // Resolve the selected field's type so we can filter operators & render the right value input
    const selectedFieldMeta = availableFields.find((f) => f.key === cond.field_key);
    const resolvedFieldType = selectedFieldMeta?.field_type;
    const dropdownOptions: string[] = Array.isArray(selectedFieldMeta?.validation_rules?.in)
      ? selectedFieldMeta!.validation_rules.in
      : [];

    // Effective operators: filtered by field type when a field is selected
    const effectiveOperators =
      meta && meta.requires_field_ref && resolvedFieldType
        ? getOperatorsForFieldType(resolvedFieldType, meta.operators)
        : meta?.operators ?? [];

    // Value input renderer — type-aware
    const renderValueInput = () => {
      if (!meta || meta.value_type === 'none') return null;
      if (cond.operator === 'is_empty' || cond.operator === 'is_not_empty') return null;
      if (!cond.operator) return null;

      if (cond.operator === 'range') {
        return (
          <Space size={4} style={{ flex: 1 }}>
            <InputNumber size="small" placeholder="Min" value={(cond.value as any)?.min}
              onChange={(v) => patchCondition(cond.id, { value: { ...((cond.value as any) ?? {}), min: v } })}
              style={{ width: '100%' }} />
            <Text type="secondary">–</Text>
            <InputNumber size="small" placeholder="Max" value={(cond.value as any)?.max}
              onChange={(v) => patchCondition(cond.id, { value: { ...((cond.value as any) ?? {}), max: v } })}
              style={{ width: '100%' }} />
          </Space>
        );
      }

      // Multi-select single-value operators: includes / excludes
      if (cond.operator === 'includes' || cond.operator === 'excludes') {
        if (resolvedFieldType === 'dropdown' && dropdownOptions.length > 0) {
          return (
            <Select
              size="small"
              style={{ flex: 1 }}
              placeholder={cond.operator === 'includes' ? 'Value to include…' : 'Value to exclude…'}
              value={cond.value ?? undefined}
              onChange={(v) => patchCondition(cond.id, { value: v })}
              allowClear
              options={dropdownOptions.map((o) => ({ value: o, label: o }))}
            />
          );
        }
        return (
          <Input
            size="small"
            style={{ flex: 1 }}
            placeholder="Value…"
            value={cond.value ?? ''}
            onChange={(e) => patchCondition(cond.id, { value: e.target.value })}
          />
        );
      }

      // Multi-select exact-match operator: must pick the full expected set
      if (cond.operator === 'equals_exactly') {
        if (resolvedFieldType === 'dropdown' && dropdownOptions.length > 0) {
          return (
            <Select
              mode="multiple"
              size="small"
              style={{ flex: 1 }}
              placeholder="Selection must be exactly these values…"
              value={Array.isArray(cond.value) ? cond.value : []}
              onChange={(v) => patchCondition(cond.id, { value: v })}
              options={dropdownOptions.map((o) => ({ value: o, label: o }))}
            />
          );
        }
        return (
          <Select
            mode="tags"
            size="small"
            style={{ flex: 1 }}
            placeholder="Selection must be exactly these values…"
            value={Array.isArray(cond.value) ? cond.value : []}
            onChange={(v) => patchCondition(cond.id, { value: v })}
          />
        );
      }

      if (cond.operator === 'in' || cond.operator === 'not_in' ||
          cond.operator === 'contains_any' || cond.operator === 'contains_all') {
        if (resolvedFieldType === 'dropdown' && dropdownOptions.length > 0) {
          return (
            <Select
              mode="multiple"
              size="small"
              style={{ flex: 1 }}
              placeholder={
                cond.operator === 'contains_all'
                  ? 'All of these must be selected…'
                  : 'Any of these selected…'
              }
              value={Array.isArray(cond.value) ? cond.value : []}
              onChange={(v) => patchCondition(cond.id, { value: v })}
              options={dropdownOptions.map((o) => ({ value: o, label: o }))}
            />
          );
        }
        return (
          <Select
            mode="tags"
            size="small"
            style={{ flex: 1 }}
            placeholder="Type values, press Enter…"
            value={Array.isArray(cond.value) ? cond.value : []}
            onChange={(v) => patchCondition(cond.id, { value: v })}
          />
        );
      }

      // Field-type-specific single-value inputs
      if (resolvedFieldType === 'number' || (!resolvedFieldType && meta.value_type === 'number')) {
        return (
          <InputNumber
            size="small"
            style={{ flex: 1 }}
            placeholder="Value…"
            value={cond.value}
            onChange={(v) => patchCondition(cond.id, { value: v })}
          />
        );
      }

      if (resolvedFieldType === 'boolean') {
        return (
          <Select
            size="small"
            style={{ flex: 1 }}
            placeholder="Select value…"
            value={cond.value}
            onChange={(v) => patchCondition(cond.id, { value: v })}
            options={[
              { value: true,  label: 'True' },
              { value: false, label: 'False' },
            ]}
          />
        );
      }

      if (resolvedFieldType === 'dropdown') {
        return (
          <Select
            size="small"
            style={{ flex: 1 }}
            placeholder={dropdownOptions.length ? 'Select value…' : 'Type value…'}
            value={cond.value}
            onChange={(v) => patchCondition(cond.id, { value: v })}
            allowClear
            options={dropdownOptions.map((o) => ({ value: o, label: o }))}
          />
        );
      }

      if (resolvedFieldType === 'date') {
        return (
          <Input
            size="small"
            style={{ flex: 1 }}
            placeholder="YYYY-MM-DD"
            value={cond.value ?? ''}
            onChange={(e) => patchCondition(cond.id, { value: e.target.value })}
          />
        );
      }

      if (resolvedFieldType === 'datetime') {
        return (
          <Input
            size="small"
            style={{ flex: 1 }}
            placeholder="YYYY-MM-DD HH:mm:ss"
            value={cond.value ?? ''}
            onChange={(e) => patchCondition(cond.id, { value: e.target.value })}
          />
        );
      }

      // Catalog enum (e.g. sample_status)
      if (meta.value_type === 'enum' && meta.enum_options) {
        return (
          <Select
            size="small"
            style={{ flex: 1 }}
            placeholder="Select value…"
            value={cond.value}
            onChange={(v) => patchCondition(cond.id, { value: v })}
            options={meta.enum_options}
          />
        );
      }

      // Default: plain text
      return (
        <Input
          size="small"
          style={{ flex: 1 }}
          placeholder="Value…"
          value={cond.value ?? ''}
          onChange={(e) => patchCondition(cond.id, { value: e.target.value })}
        />
      );
    };

    return (
      <Card
        key={cond.id}
        size="small"
        style={{
          marginBottom: 8,
          border: '1px solid var(--mg-border)',
          borderRadius: 8,
        }}
        styles={{ body: { padding: '10px 12px' } }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {/* Row header: index + type select + delete */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Tag
              style={{
                minWidth: 22,
                textAlign: 'center',
                background: 'var(--mg-bg-subtle)',
                border: '1px solid var(--mg-border)',
                fontWeight: 700,
                fontSize: 11,
              }}
            >
              {idx + 1}
            </Tag>
            <Select
              value={cond.type}
              onChange={(v) => patchCondition(cond.id, { type: v as ProtocolConditionType })}
              options={typeOptions}
              style={{ flex: 1 }}
              size="small"
              placeholder="Select condition type…"
              showSearch
              optionFilterProp="label"
            />
            <Tooltip title="Remove condition">
              <Button
                type="text"
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => removeCondition(cond.id)}
              />
            </Tooltip>
          </div>

          {/* Description hint */}
          {meta && (
            <Text type="secondary" style={{ fontSize: 11, lineHeight: 1.4 }}>
              {meta.description}
            </Text>
          )}

          {/* Step reference */}
          {meta?.requires_step_ref && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text style={{ fontSize: 11, width: 70, flexShrink: 0 }} type="secondary">
                Step ref
              </Text>
              <Select
                value={cond.step_node_id}
                onChange={(v) => patchCondition(cond.id, { step_node_id: v })}
                style={{ flex: 1 }}
                size="small"
                placeholder="Select upstream step…"
                allowClear
                options={upstreamNodes.map((n) => ({
                  value: n.id,
                  label: (
                    <Space size={4}>
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: PROTOCOL_NODE_CONFIG[n.data.step_type]?.color,
                          display: 'inline-block',
                        }}
                      />
                      <span>{n.data.label || PROTOCOL_NODE_CONFIG[n.data.step_type]?.label}</span>
                      {n.data.step_number != null && (
                        <Text type="secondary" style={{ fontSize: 10 }}>
                          (Step {n.data.step_number})
                        </Text>
                      )}
                    </Space>
                  ),
                }))}
                notFoundContent={
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    No upstream steps found. Connect lab steps before this node.
                  </Text>
                }
              />
            </div>
          )}

          {/* Field reference */}
          {meta?.requires_field_ref && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text style={{ fontSize: 11, width: 70, flexShrink: 0 }} type="secondary">
                Field
              </Text>
              <Select
                value={cond.field_key}
                onChange={(v) => patchCondition(cond.id, { field_key: v })}
                style={{ flex: 1 }}
                size="small"
                placeholder="Select field…"
                allowClear
                disabled={!cond.step_node_id}
                options={availableFields.map((f) => ({ value: f.key, label: f.label }))}
                notFoundContent={
                  cond.step_node_id ? (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      No fields defined on selected step. Add fields in the step's Data Fields tab.
                    </Text>
                  ) : (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      Select a step first
                    </Text>
                  )
                }
              />
            </div>
          )}

          {/* Operator + value */}
          {meta && effectiveOperators.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <Text style={{ fontSize: 11, width: 70, flexShrink: 0, paddingTop: 5 }} type="secondary">
                Operator
              </Text>
              <div style={{ display: 'flex', flex: 1, gap: 8 }}>
                <Select
                  value={cond.operator}
                  onChange={(v) => patchCondition(cond.id, { operator: v as ConditionOperator, value: undefined })}
                  style={{ flex: '0 0 160px' }}
                  size="small"
                  placeholder="Operator…"
                  options={effectiveOperators.map((op) => ({
                    value: op,
                    label: OPERATOR_LABELS[op] ?? op,
                  }))}
                />
                {renderValueInput()}
              </div>
            </div>
          )}

          {/* Auto-generated plain-English label */}
          {cond.label && (
            <div
              style={{
                background: 'var(--mg-bg-subtle)',
                borderRadius: 4,
                padding: '4px 8px',
                fontSize: 11,
                color: 'var(--mg-text-secondary)',
                borderLeft: '3px solid var(--mg-primary)',
              }}
            >
              <InfoCircleOutlined style={{ marginRight: 4 }} />
              {cond.label}
            </div>
          )}
        </div>
      </Card>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingBottom: 16 }}>
      
      {/* Group operator */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Text strong style={{ fontSize: 12 }}>
          Match
        </Text>
        <Segmented
          size="small"
          value={cfg.group_operator}
          onChange={(v) => onChange({ ...cfg, group_operator: v as 'AND' | 'OR' })}
          options={[
            { value: 'AND', label: 'ALL conditions (AND)' },
            { value: 'OR',  label: 'ANY condition (OR)' },
          ]}
        />
      </div>

      {/* Evaluation scope */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Text strong style={{ fontSize: 12 }}>
            Evaluate per
          </Text>
          <Segmented
            size="small"
            value={cfg.evaluation_scope ?? 'per_group'}
            onChange={(v) => onChange({ ...cfg, evaluation_scope: v as 'per_group' | 'per_sample' })}
            options={[
              { value: 'per_group',  label: 'Group (all samples)' },
              { value: 'per_sample', label: 'Individual sample' },
            ]}
          />
        </div>
        <Text type="secondary" style={{ fontSize: 11, marginTop: -8 }}>
          {(cfg.evaluation_scope ?? 'per_group') === 'per_group'
            ? 'ALL samples must satisfy the conditions for the YES branch. If any sample fails, all go to NO.'
            : 'Each sample is evaluated independently. Samples that pass go to YES; the rest go to NO.'}
        </Text>
      </div>

      {/* Condition rows */}
      <div>
        {cfg.conditions.length === 0 ? (
          <div
            style={{
              border: '1.5px dashed var(--mg-border)',
              borderRadius: 8,
              padding: '24px 16px',
              textAlign: 'center',
              color: 'var(--mg-text-disabled)',
            }}
          >
            <BranchesOutlined style={{ fontSize: 28, marginBottom: 8 }} />
            <div style={{ fontSize: 13 }}>No conditions configured</div>
            <div style={{ fontSize: 11, marginTop: 4 }}>
              Without conditions this node always routes to <strong>YES</strong>.
            </div>
          </div>
        ) : (
          cfg.conditions.map((c, i) => renderConditionRow(c, i))
        )}
      </div>

      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={addCondition}
        style={{ width: '100%' }}
        size="small"
      >
        Add Condition
      </Button>

      <Divider style={{ margin: '4px 0' }} />

    </div>
  );
};

export default ConditionalNodeEditor;
